import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "@/lib/auth";

const formContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const formItem = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" as const },
  },
};

export default function LoginPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // 보호된 페이지에서 넘어왔다면 로그인 후 원래 가려던 곳으로 돌려보낸다.
  const redirectTo = (location.state as { from?: string } | null)?.from ?? "/mypage";

  useEffect(() => {
    if (!loading && user) navigate(redirectTo, { replace: true });
  }, [loading, user, navigate, redirectTo]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setErrorMsg("이메일과 비밀번호를 모두 입력해주세요.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    const { error } = await signIn(form.email.trim(), form.password);
    if (error) {
      setErrorMsg(error);
      setStatus("error");
      return;
    }
    setStatus("idle");
    navigate(redirectTo, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[440px] mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="text-center mb-10"
          >
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <div className="w-9 h-9 rounded-lg bg-[#C4956A] flex items-center justify-center">
                <i className="ri-auction-line text-lg text-white"></i>
              </div>
              <span className="text-xl font-bold text-[#1C1917]">강스터디</span>
            </Link>
            <h1 className="text-2xl font-bold text-[#1C1917] mb-2">로그인</h1>
            <p className="text-sm text-[#8C8780]">
              강스터디에 오신 것을 환영합니다
            </p>
          </motion.div>

          {/* Login Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="bg-white rounded-2xl border border-[#E8E5E0] p-6 md:p-8"
          >
            <form onSubmit={handleSubmit}>
              <motion.div variants={formContainer} initial="hidden" animate="visible">
                {/* Error Message */}
                <AnimatePresence>
                  {status === "error" && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.25 }}
                      className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
                    >
                      {errorMsg}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Email */}
                <motion.div variants={formItem} className="mb-4">
                  <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                    이메일
                  </label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#8C8780]"></i>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="example@email.com"
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
                    />
                  </div>
                </motion.div>

                {/* Password */}
                <motion.div variants={formItem} className="mb-2">
                  <label className="block text-sm font-medium text-[#1C1917] mb-1.5">
                    비밀번호
                  </label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#8C8780]"></i>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      placeholder="비밀번호를 입력해주세요"
                      className="w-full pl-10 pr-12 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8780] hover:text-[#5C5650] transition-colors"
                    >
                      <i className={`text-sm ${showPassword ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                    </button>
                  </div>
                </motion.div>

                {/* Forgot Password */}
                <motion.div variants={formItem} className="flex justify-end mb-6">
                  <button type="button" className="text-xs text-[#C4956A] hover:text-[#B08050] transition-colors">
                    비밀번호를 잊으셨나요?
                  </button>
                </motion.div>

                {/* Submit */}
                <motion.div variants={formItem} className="mb-5">
                  <motion.button
                    type="submit"
                    disabled={status === "loading"}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3 text-sm font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {status === "loading" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        로그인 중...
                      </>
                    ) : (
                      "로그인"
                    )}
                  </motion.button>
                </motion.div>

                {/* Divider */}
                <motion.div variants={formItem} className="flex items-center gap-3 mb-5">
                  <div className="flex-1 h-px bg-[#E8E5E0]"></div>
                  <span className="text-xs text-[#B8B3AE]">또는</span>
                  <div className="flex-1 h-px bg-[#E8E5E0]"></div>
                </motion.div>

                {/* Social Login Buttons */}
                <motion.div variants={formItem} className="space-y-2.5">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-2.5 text-sm font-medium border border-[#E8E5E0] rounded-full text-[#5C5650] hover:border-[#FEE500] hover:bg-[#FEE500]/5 transition-all whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-kakao-talk-fill text-[#3A1D1D] text-base"></i>
                    카카오로 로그인
                  </motion.button>
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-2.5 text-sm font-medium border border-[#E8E5E0] rounded-full text-[#5C5650] hover:border-[#03C75A] hover:bg-[#03C75A]/5 transition-all whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <span className="text-[#03C75A] font-bold text-base">N</span>
                    네이버로 로그인
                  </motion.button>
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-2.5 text-sm font-medium border border-[#E8E5E0] rounded-full text-[#5C5650] hover:border-[#1C1917] hover:bg-[#1C1917]/5 transition-all whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-google-fill text-[#4285F4] text-base"></i>
                    Google로 로그인
                  </motion.button>
                </motion.div>
              </motion.div>
            </form>
          </motion.div>

          {/* Register Link */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="text-center mt-6 text-sm text-[#8C8780]"
          >
            아직 회원이 아니신가요?{" "}
            <Link to="/register" className="text-[#C4956A] font-medium hover:text-[#B08050] transition-colors">
              회원가입
            </Link>
          </motion.p>
        </div>
      </main>
      <Footer />
    </div>
  );
}